import { Dispatch, AnyAction } from 'redux';
import {
  PDFItemType,
  PrinterApprovalStatus,
  PDFType,
  PDFSettingStepOption,
  PDFSettingStep,
  PDFHeaderLayout,
} from '@core/enums/project';
import ApiService from '@core/services/ApiService';
import { Api } from '@core/types';
import { ThunkDispatch } from 'redux-thunk';
import { FormattedNumberConfig, FormValue, SetPDFModal, Template, ProjectProduct, Category, Product } from '@core/types/models';
import {
  GENERATE_PDF,
  GENERATE_PDF_SUCCESS,
  SEND_FOR_APPROVAL,
  SEND_FOR_APPROVAL_SUCCESS,
  SEND_FOR_APPROVAL_ERROR,
  SET_PDF_MODAL,
  CLEAR_PDF,
  DOWNLOAD_PDF,
  SET_ITEM_TYPE,
  FETCH_PROJECT_PDF,
  FETCH_PROJECT_PDF_SUCCESS,
  FETCH_PROJECT_PDF_ERROR,
  RESET_PDF,
  SET_CURRENCY,
  SET_FIELD,
  SET_REQUIRES_REVIEW,
  SET_ORDER_VISITED,
  SET_TEMPLATES,
  SET_TEMPLATE,
  SET_CURRENT_PRODUCT_INDEX,
  SET_SETTING_STEPS,
  SET_SETTING_STEP,
  CLEAR_JOURNEY_PDF_DATA,
} from './actions';
import { fetchPdfs } from '../admin/actioncreators';
import { updateProjectError } from '../projects/actioncreators';
import { IGeneratePDFRequest, IUpdateFlyerDataRequest } from '@core/types/api';
import { allOrderDetailFields } from 'config';

type TFunction = (key: string) => string;

const getInitialFormData = (template: Template | null, t: TFunction): Record<string, FormValue> => {
  if (!template) return {};
  return template.fields.reduce((acc: Record<string, FormValue>, cur: any) => {
    if (cur.value) {
      acc[cur.name] = cur.value;
    } else if (cur.valueRes) {
      acc[cur.name] = t(cur.valueRes);
    }
    return acc;
  }, {});
};

// Helper to get steps for a template
const getStepsForTemplate = (template: Template | null, PDFSettingStepOptions: PDFSettingStepOption[]): PDFSettingStepOption[] => {
  return PDFSettingStepOptions.reduce((acc: PDFSettingStepOption[], cur: PDFSettingStepOption) => {
    const fieldsForStep = template?.fields.filter((x: any) => x.step === cur.id) || [];
    if (cur.id === PDFSettingStep.FORMAT || cur.id === PDFSettingStep.DOWNLOAD_DETAILS || fieldsForStep.length > 0) {
      acc.push(cur);
    }
    return acc;
  }, []);
};

// Thunk: handle item type change (sets templates, template, steps, formData, resets product index)
export const handlePdfItemTypeChange =
  (
    itemType: PDFItemType | null,
    isPrint: boolean,
    type: PDFType,
    t: TFunction,
    PDFFlyerTemplateOptions: Template[],
    PDFTagTemplateOptions: Template[],
    PDFTagPrintTemplateOptions: Template[],
    PDFSettingStepOptions: PDFSettingStepOption[],
  ) =>
  (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({ type: SET_ITEM_TYPE, payload: itemType });

    dispatch(handleSetRequiresReview(type === PDFType.SendToPrinters));
    dispatch(handleSetOrderVisited(false));

    let templates = null;
    if (itemType === PDFItemType.Flyer) {
      templates = PDFFlyerTemplateOptions;
    } else {
      templates = isPrint ? PDFTagPrintTemplateOptions : PDFTagTemplateOptions;
    }

    const template = templates[0];
    dispatch(handleSetTemplates(templates));
    dispatch(handleSetFlyerTemplate(template, t));
    dispatch(handleSetCurrentProductIndex(0));
    // Set steps
    const steps = getStepsForTemplate(template, PDFSettingStepOptions);
    dispatch(handleSetSettingSteps(steps));
    dispatch(handleSetCurrentStep(0));
  };

// Thunk: handle template change (sets steps, ensures current step is valid)
export const handlePdfTemplateChange =
  (template: Template | null, currentStep: number | null, PDFSettingStepOptions: PDFSettingStepOption[]) =>
  (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    const steps = getStepsForTemplate(template, PDFSettingStepOptions);
    dispatch(handleSetSettingSteps(steps));
    if (currentStep === null || !steps.some((step) => step.id === currentStep)) {
      dispatch(handleSetCurrentStep(0));
    }
  };

// Thunk: update products when project or categories change
export const handlePdfProductsUpdate =
  (
    projectProducts: ProjectProduct[] | undefined,
    categories: Category[] | undefined,
    ProductService: any,
    setFlyerProducts: (products: Product[]) => void,
  ) =>
  (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    if (projectProducts && categories) {
      const products: Product[] = projectProducts.flatMap((x) => {
        const productInfo = ProductService.flatten(categories).find((y: any) => y.variantAgilityID === x.productID);
        return productInfo
          ? Array.from({ length: x.quantity }, (_, i) => ({
              ...productInfo,
              id: `${x.productID}-${i}`,
              formData: {},
            }))
          : [];
      });
      setFlyerProducts(products);
    } else {
      setFlyerProducts([]);
    }
  };

const fetchPdf = (params: Api.IFetchProjectPdfRequest) =>
  new Promise<Api.IBaseResponse<Api.IFetchProjectPdfResponse>>(async (resolve) => {
    const handle = window.setInterval(async () => {
      const response = await ApiService.request<Api.IFetchProjectPdfResponse>({
        controller: 'project',
        method: Api.Method.GET,
        slug: 'get-project-pdf',
        bearerToken: params.bearerToken,
        params: {
          projectPDFID: params.projectPDFID,
        },
      });

      if (response.data?.projectPDF !== null && response.data?.projectPDF.printerApprovalStatus !== PrinterApprovalStatus.Generating) {
        window.clearTimeout(handle);
        resolve(response);
        return;
      }
    }, 3000);
  });

export const fetchProjectPdf = (params: Api.IFetchProjectPdfRequest) => async (dispatch: Dispatch) =>
  new Promise(async (resolve) => {
    // Initial action to trigger loaders
    dispatch({
      type: FETCH_PROJECT_PDF,
    });

    // Do the api request
    const response = await fetchPdf(params);

    // Dispatch any errors
    if (!response.success || response.errors.length > 0) {
      return dispatch({
        type: FETCH_PROJECT_PDF_ERROR,
        payload: response.errors,
      });
    }

    // dispatch the final response data
    return dispatch({
      type: FETCH_PROJECT_PDF_SUCCESS,
      payload: response.data,
    });
  });

export const generatePDF =
  (params: Api.IUpdateFlyerAndGeneratePDFRequest, type: PDFType) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch({
      type: GENERATE_PDF,
      payload: type,
    });

    const generateUUID = () => {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    };

    const { flyerData, bearerToken, ...remainingParams } = params;

    // get values from flyerData.FormData (Record<string, FormValue>), where key matches name in allOrderDetailFields
    const orderDetails: Record<string, FormValue> = {};
    allOrderDetailFields.forEach((field) => {
      const value = flyerData.formData[field.name];
      if (value !== undefined) {
        orderDetails[field.name] = value;
      }
    });

    let flyerGUID = null;
    let generatePdfResponse = null;

    if (params.flyerData.itemType === PDFItemType.Flyer) {
      flyerGUID = params.flyerGUID || generateUUID();

      generatePdfResponse = await ApiService.request<Api.IGeneratePDFResponse>({
        controller: 'project',
        method: Api.Method.POST,
        slug: 'update-flyer-data',
        bearerToken,
        params: {
          ...remainingParams,
          ...orderDetails,
          flyerGUID,
          flyerData: JSON.stringify({
            ...params.flyerData,
            template: params.flyerData.template.key,
            heroImage: undefined,
            features: undefined,
          }),
          flyerProducts:
            params.flyerData.products
              ?.map((x) => x.variantAgilityID)
              .filter((x) => x !== null)
              .slice(0, params.flyerData.template.products) || [],
          cultureCode: params.flyerData.formData['cultureCode'],
          isPrint: type === PDFType.SendToPrinters,
          templateName: params.flyerData.template.value,
        } as IUpdateFlyerDataRequest,
      });

      if (!generatePdfResponse.success || generatePdfResponse.errors.length > 0) {
        updateProjectError(generatePdfResponse.errors);
        return;
      }
    } else if (params.flyerData.itemType === PDFItemType.Tags) {
      // Do the api request
      generatePdfResponse = await ApiService.request<Api.IGeneratePDFResponse>({
        controller: 'project',
        method: Api.Method.POST,
        slug: 'generate-pdf-from-project',
        bearerToken,
        params: {
          ...remainingParams,
          ...orderDetails,
          size: params.flyerData.template.key,
          headerLayout: params.flyerData.formData['showMilwaukeeLogo'] ? PDFHeaderLayout.Normal : PDFHeaderLayout.NoLogo,
          fullDownload: type === PDFType.Download ? true : false,
          cultureCode: params.flyerData.formData['cultureCode'],
          isPrint: type === PDFType.SendToPrinters,
          templateName: params.flyerData.template.value,
        } as IGeneratePDFRequest,
      });
    }

    // Await PDF generation
    const fetchPdfResponse = await fetchPdf({
      projectPDFID: (generatePdfResponse && generatePdfResponse.data && generatePdfResponse.data.projectPDFID) || -1,
      bearerToken: params.bearerToken,
    });

    // Dispatch any errors
    if (!fetchPdfResponse.success || fetchPdfResponse.errors.length > 0) {
      updateProjectError(fetchPdfResponse.errors);
    }

    // dispatch a success
    return dispatch({
      type: GENERATE_PDF_SUCCESS,
      payload: {
        response: fetchPdfResponse.data,
        type: type,
      },
    });
  };

export const SendForApproval =
  (params: Api.ISendForApprovalRequest, projectGUID: string, userCultures: string[]) =>
  async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    // Initial action to trigger loaders
    dispatch({
      type: SEND_FOR_APPROVAL,
    });

    // Do the api request
    const response = await ApiService.request<boolean>({
      controller: 'project',
      method: Api.Method.GET,
      slug: 'send-pdf-for-approval',
      bearerToken: params.bearerToken,
      params: {
        projectPDFID: params.projectPDFID,
        cultureCode: params.cultureCode,
      },
    });

    // Dispatch any errors
    if (!response.success || response.errors.length > 0) {
      return dispatch({
        type: SEND_FOR_APPROVAL_ERROR,
        payload: response.errors,
      });
    }

    // dispatch a success
    dispatch({
      type: SEND_FOR_APPROVAL_SUCCESS,
      payload: response.data,
    });

    dispatch(clearPDF(PDFType.SendToPrinters));

    dispatch(
      fetchPdfs({
        bearerToken: params.bearerToken,
        cultureCode: userCultures,
        status: PrinterApprovalStatus.AwaitingApproval,
      }),
    );

    return dispatch(
      setPDFModal({
        isGenerating: false,
        isOpen: true,
        showConfirmation: true,
        isSendingForApproval: false,
        pdfType: PDFType.SendToPrinters,
      }),
    );
  };

export const setPdfItemType = (pdfItemType: PDFItemType | null) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({
    type: SET_ITEM_TYPE,
    payload: pdfItemType,
  });
};

export const setPDFModal = (settings: SetPDFModal) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  dispatch({
    type: SET_PDF_MODAL,
    payload: settings,
  });
};

export const clearPDF = (type: PDFType) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  dispatch({
    type: CLEAR_PDF,
    payload: type,
  });

  dispatch(
    setPDFModal({
      isGenerating: false,
      isOpen: false,
      showConfirmation: false,
      isSendingForApproval: false,
      pdfType: type,
    }),
  );
};

export const downloadPDF = (url: string) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  dispatch({
    type: DOWNLOAD_PDF,
  });

  // Download the URL using an anchor tag
  const link = document.createElement('a');
  link.target = '_blank';
  link.download = 'FactTags.pdf';
  link.href = url;
  document.body.appendChild(link);
  link.click();
  link.remove();

  dispatch(clearPDF(PDFType.Download));

  return dispatch(
    setPDFModal({
      isGenerating: false,
      isOpen: false,
      showConfirmation: false,
      isSendingForApproval: false,
      pdfType: PDFType.Download,
    }),
  );
};

export const resetPDF = () => {
  return {
    type: RESET_PDF,
  };
};

export const setCurrency = (currency: FormattedNumberConfig) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  dispatch({
    type: SET_CURRENCY,
    payload: currency,
  });
};

export const setField = (field: string, value: FormValue) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  dispatch({
    type: SET_FIELD,
    payload: { field, value },
  });
};

export const handleSetRequiresReview = (requiresReview: boolean) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({
    type: SET_REQUIRES_REVIEW,
    payload: requiresReview,
  });
};

export const handleSetOrderVisited = (orderVisited: boolean) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({
    type: SET_ORDER_VISITED,
    payload: orderVisited,
  });
};

export const handleSetTemplates = (templates: Template[] | null) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({
    type: SET_TEMPLATES,
    payload: templates,
  });
};

export const handleSetCurrentProductIndex = (index: number) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({
    type: SET_CURRENT_PRODUCT_INDEX,
    payload: index,
  });
};

export const handleSetSettingSteps = (steps: PDFSettingStepOption[]) => (dispatch: Dispatch<AnyAction>) => {
  dispatch({
    type: SET_SETTING_STEPS,
    payload: steps,
  });
};

export const handleSetCurrentStep = (step: number) => (dispatch: Dispatch<AnyAction>) => {
  if (step === PDFSettingStep.ORDER) {
    handleSetOrderVisited(true)(dispatch);
  }
  dispatch({
    type: SET_SETTING_STEP,
    payload: step,
  });
};

// Thunk: handle template change (sets template and form data)
export const handleSetFlyerTemplate = (template: Template | null, t: TFunction) => (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  const updatedFormData = getInitialFormData(template, t);
  Object.entries(updatedFormData).forEach(([key, value]) => {
    dispatch(setField(key, value));
  });

  dispatch({
    type: SET_TEMPLATE,
    payload: template,
  });
};

export const handleClose = (type: PDFType) => async (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
  clearPDF(type)(dispatch);
  dispatch({ type: CLEAR_JOURNEY_PDF_DATA });
};
